import { invert } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { TabPanel } from 'react-tabs';
import { colors } from '../../../../../../../../../colorVars';
import { modifySoftOrderProvideDetails } from '../../../../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/updateTradePlanMessages/modifySoftOrders';
import { useWebsocketStore } from '../../../../../../../../../stores/websocketStore/websocketProvider';
import { CancelOrderButton } from '../../../../../../../../atomic/buttons/cancelOrderButton/CancelOrderButton';
import IncrementButton from '../../../../../../../../atomic/buttons/incrementButton/IncrementButton';
import { EditableTableFieldNoOnBlurUpdate } from '../../../../../../../../atomic/inputs/editableTableField/EditableFieldNoOnBlurUpdate';
import { Body3 } from '../../../../../../../../atomic/text/Text';
import style from '../../IndividualTacticStyle.module.css';
import { CandlePatternTacticPanelHeader } from './CandlePatternTacticPanelHeader';
const sequenceMap = {
  INSIDE_CANDLE: '1', 
  OUTSIDE_CANDLE: '3',
  BREAK_HIGH: '2U',
  BREAK_LOW: '2D',
  FAIL_TO_BREAK_HIGH: 'FBH',
  FAIL_TO_BREAK_LOW: 'FBL',
  BREAK_CANDLE: '2',
  OUTSIDE_CANDLE_DOWN: '3D',
  OUTSIDE_CANDLE_UP: '3U',
};
const reverseSequenceMap = invert(sequenceMap);

export const CandlePatternTacticPanel = observer(
  // eslint-disable-next-line no-unused-vars
  ({ tradePlanId, addTab, ...rest }) => {
    const { tradePlanStore } = useWebsocketStore();
    const tacticId =
      tradePlanStore.tradePlans[tradePlanId].setupDetails['CANDLE_PATTERN']
        ?.tacticId ?? null;
    const softOrder = tradePlanStore.softOrdersById[tacticId] ?? null;
    const patterns = softOrder?.value.tacticValue.patterns ?? null;
    const [addPatternValue, setAddPatternValue] = useState('Enter pattern...');
    const [textColor, setTextColor] = useState(colors.lightGrey);

    const timeoutRef = useRef(null);
    
    useEffect(() => {
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, []);

    if (!tacticId || !softOrder) return null;

    return (
      <TabPanel {...rest}>
        <div className={style.tableContainer}>
          <table className={style.table}>
            <CandlePatternTacticPanelHeader />
            <tbody>
              {patterns &&
                patterns.map((pattern, index) => {
                  return (
                    <tr
                      key={pattern.reduce((acc, item) => {
                        return acc + item.candleSequence;
                      }, '')}
                    >
                      <td className={style.labelTd}>
                        <Body3 fontWeight={700} color={colors.lightGrey}>
                          {index + 1}
                        </Body3>
                      </td>
                      <td className={style.labelTd}>
                        <Body3 fontWeight={700} color={colors.lightGrey}>
                          {pattern
                            .map(
                              (pattern) => sequenceMap[pattern.candleSequence]
                            )
                            .join('-')}
                        </Body3>
                      </td>
                      <td className={style.inputTd}>
                        <CancelOrderButton
                          margin="5px 0 0 0"
                          width={15}
                          clickHandler={() =>
                            modifySoftOrderProvideDetails({
                              softOrder,
                              details: { removePattern: pattern },
                            })
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Add Pattern
                  </Body3>
                </td>
                <td className={style.labelTd}>
                  <EditableTableFieldNoOnBlurUpdate
                    value={addPatternValue}
                    setValue={setAddPatternValue}
                    color={textColor}
                    fontSize="0.694rem"
                    inputType="text"
                    backgroundColor="inherit"
                  />
                </td>
                <td className={style.inputTd}>
                  <IncrementButton
                    paddingPercent={10}
                    margin="3px 0 0 0"
                    color={colors.white}
                    backgroundColor={colors.blue}
                    width={10}
                    stroke={2}
                    handleClick={() => {
                      const addPatternCopy = addPatternValue;
                      const sequenceArr = addPatternValue
                        .split('-')
                        .map((value, index) => {
                          if (!reverseSequenceMap[value.toUpperCase()]) {
                            setAddPatternValue('Invalid Pattern');
                            setTextColor(colors.red);
                            const timer = setTimeout(() => {
                              setAddPatternValue(addPatternCopy);
                              setTextColor(colors.lightGrey);
                            }, 500);
                            timeoutRef.current = timer;
                          } else {
                            return {
                              candleSequence:
                                reverseSequenceMap[value.toUpperCase()],
                              terminateOnFail: false,
                              restartOnFail: index !== 0,
                            };
                          }
                        });
                      modifySoftOrderProvideDetails({
                        softOrder,
                        details: { addPattern: sequenceArr },
                      });
                    }}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </TabPanel>
    );
  }
);

CandlePatternTacticPanel.tabsRole = 'TabPanel';
